import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  author?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://cornerstonepartners.com.br";
const DEFAULT_IMAGE = "https://lovable.dev/opengraph-image-p98pqg.png";

// Organization schema - used on homepage
export const organizationSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Cornerstone Partners",
    "url": BASE_URL,
    "logo": `${BASE_URL}/favicon.png`,
    "description": "Boutique de M&A especializada em fusões, aquisições, valuation e consultoria empresarial.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "BR"
    },
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "availableLanguage": ["Portuguese"]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Navegação Principal",
    "hasPart": [
      { "@type": "WebPage", "name": "M&A - Fusões e Aquisições", "url": `${BASE_URL}/servicos/ma` },
      { "@type": "WebPage", "name": "Valuation - Avaliação de Empresas", "url": `${BASE_URL}/servicos/valuation` },
      { "@type": "WebPage", "name": "Auditoria", "url": `${BASE_URL}/servicos/auditoria` },
      { "@type": "WebPage", "name": "Finance Office - Consultoria Financeira", "url": `${BASE_URL}/servicos/finance-office` },
      { "@type": "WebPage", "name": "Blog", "url": `${BASE_URL}/blog` }
    ]
  }
];

// Service schema generator
export const createServiceSchema = (
  name: string,
  description: string,
  url: string
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": description,
  "url": `${BASE_URL}${url}`,
  "provider": {
    "@type": "Organization",
    "name": "Cornerstone Partners",
    "url": BASE_URL
  },
  "areaServed": {
    "@type": "Country",
    "name": "Brazil"
  },
  "serviceType": "Financial Advisory"
});

// Article schema generator
export const createArticleSchema = (
  title: string,
  description: string,
  url: string,
  image: string,
  publishedTime: string,
  authorName: string
) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "url": `${BASE_URL}${url}`,
  "image": image,
  "datePublished": publishedTime,
  "author": {
    "@type": "Person",
    "name": authorName
  },
  "publisher": {
    "@type": "Organization",
    "name": "Cornerstone Partners",
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/favicon.png`
    }
  }
});

// BreadcrumbList schema generator
export const createBreadcrumbSchema = (
  items: { name: string; url: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${BASE_URL}${item.url}`
  }))
});

// FAQPage schema generator
export const createFAQSchema = (
  faqs: { question: string; answer: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const SEO = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  publishedTime,
  author,
  jsonLd,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to update or create meta tag
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Basic meta tags
    setMeta("description", description);
    setMeta("author", author || "Cornerstone Partners");

    // Open Graph
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:image", image, true);
    setMeta("og:type", type, true);
    if (url) setMeta("og:url", `${BASE_URL}${url}`, true);

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    // Article specific
    if (type === "article" && publishedTime) {
      setMeta("article:published_time", publishedTime, true);
      if (author) setMeta("article:author", author, true);
    }

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (url) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", `${BASE_URL}${url}`);
    }

    // Handle JSON-LD
    const existingJsonLd = document.querySelector('script[data-seo-jsonld]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(
        Array.isArray(jsonLd) ? jsonLd : jsonLd
      );
      document.head.appendChild(script);
    }

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = "Cornerstone | Valuation, M&A e Consultoria";
      const jsonLdScript = document.querySelector('script[data-seo-jsonld]');
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, image, url, type, publishedTime, author, jsonLd]);

  return null;
};

export default SEO;
